import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
} from "@mui/material";
import moment from "moment";
import AIDEConsoleSecurityFilter from "./AIDEConsoleSecurityFilter";
import Autocomplete from "@material-ui/lab/Autocomplete";

const columns = [
  { id: "user_id", label: "User Id", minWidth: 180 },
  { id: "role", label: "User Role", minWidth: 100 },
  { id: "access_granted_by", label: "Access Granted By", minWidth: 150 },
  { id: "access_granted_on", label: "Access Granted On", minWidth: 150 },
  { id: "security_filter", label: "Security Filter", minWidth: 120 },
];

const roleOptions = ["owner", "admin", "user"];

const AIDEConsoleUserTable = ({
  accessList,
  accessListOriginal,
  countryBrandsData,
  onUpdateRole,
  onDeleteUser,
  onUpdateSecurityFilters,
  currentUserRole,
  theme,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterValue, setFilterValue] = useState("");
  const [filterRole, setFilterRole] = useState("All");
  const [sortAsc, setSortAsc] = useState(true);
  const [openFilter, setOpenFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Filter the access list
  const getFilteredList = () => {
    let filtered = accessList || [];

    if (filterValue) {
      const lowerFilter = filterValue.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          (item.user_id && item.user_id.toLowerCase().includes(lowerFilter)) ||
          (item.name && item.name.toLowerCase().includes(lowerFilter)),
      );
    }

    if (filterRole !== "All") {
      filtered = filtered.filter((item) => item.role === filterRole);
    }

    return filtered;
  };

  const filteredList = getFilteredList();

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSortByDate = () => {
    const sorted = [...accessList].sort((a, b) => {
      return sortAsc
        ? a.access_granted_on - b.access_granted_on
        : b.access_granted_on - a.access_granted_on;
    });
    setSortAsc(!sortAsc);
    // We need to trigger an update through the parent
    onUpdateRole(null, null, sorted);
  };

  const handleViewFilters = (row) => {
    setSelectedRow(row);
    setOpenFilter(true);
  };

  const handleCloseFilter = () => {
    setOpenFilter(false);
    setSelectedRow(null);
  };

  const handleSaveFilters = (row, filters) => {
    onUpdateSecurityFilters(row, filters);
  };

  const getAccessOptions = () => {
    if (currentUserRole === "owner" || currentUserRole === "super_admin") {
      return ["owner", "admin", "user"];
    } else if (currentUserRole === "admin") {
      return ["admin", "user"];
    }
    return [];
  };

  const accessOptions = getAccessOptions();
  const canEdit =
    currentUserRole === "owner" ||
    currentUserRole === "admin" ||
    currentUserRole === "super_admin";

  return (
    <Paper elevation={0} sx={{ width: "100%", overflow: "hidden", boxShadow: "none !important" }}>
      {/* Filter bar */}
      <div className="aide-filter-new">
        <div className="input-search">
          <i className="material-icons-outlined search-icon">search</i>
          <input
            type="text"
            className="input-label"
            placeholder="Search by User Id or Name"
            value={filterValue}
            autoComplete="off"
            onChange={(e) => {
              setFilterValue(e.target.value);
              setPage(0);
            }}
          />
          {filterValue && (
            <i
              className="material-icons-outlined cancel-icon visible"
              onClick={() => {
                setFilterValue("");
                setPage(0);
              }}
            >
              clear
            </i>
          )}
        </div>
        <div className="right-filter-div">
          <Select
            size="small"
            value={filterRole}
            onChange={(e) => {
              setFilterRole(e.target.value);
              setPage(0);
            }}
            sx={{
              minWidth: 120,
              height: 30,
              backgroundColor: "#f7f7f7",
              "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              fontFamily: '"Hind Siliguri"',
            }}
          >
            <MenuItem value="All" sx={{ fontFamily: '"Hind Siliguri"' }}>
              All Roles
            </MenuItem>
            {roleOptions.map((role) => (
              <MenuItem
                key={role}
                value={role}
                sx={{
                  textTransform: "capitalize",
                  fontFamily: '"Hind Siliguri"',
                }}
              >
                {role}
              </MenuItem>
            ))}
          </Select>
        </div>
      </div>

      <TableContainer className="aide-console-table-container">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  style={{ minWidth: column.minWidth }}
                  onClick={
                    column.id === "access_granted_on"
                      ? handleSortByDate
                      : undefined
                  }
                  sx={{
                    cursor:
                      column.id === "access_granted_on" ? "pointer" : "default",
                    userSelect: "none",
                  }}
                >
                  {column.label}
                  {column.id === "access_granted_on" && (
                    <span
                      className="material-icons-outlined"
                      style={{
                        fontSize: 14,
                        verticalAlign: "middle",
                        marginLeft: 4,
                      }}
                    >
                      {sortAsc ? "arrow_upward" : "arrow_downward"}
                    </span>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredList
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <TableRow
                  hover
                  key={row.user_id + index}
                  className={index % 2 === 0 ? "even-row" : "odd-row"}
                >
                  {/* User ID */}
                  <TableCell className="user-id-col" title={row.user_id}>
                    {row.user_id}
                  </TableCell>

                  {/* Role */}
                  <TableCell className="role-col">
                    {canEdit ? (
                      <Autocomplete
                        blurOnSelect
                        disableClearable
                        value={row.role}
                        options={accessOptions}
                        onChange={(e, newValue) => onUpdateRole(row, newValue)}
                        style={{ width: 100 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="standard"
                            size="small"
                            style={{ fontSize: 13 }}
                          />
                        )}
                      />
                    ) : (
                      <span style={{ textTransform: "capitalize" }}>
                        {row.role}
                      </span>
                    )}
                  </TableCell>

                  {/* Access Granted By */}
                  <TableCell
                    className="granted-by-col"
                    title={row.access_granted_by}
                  >
                    {row.access_granted_by}
                  </TableCell>

                  {/* Access Granted On */}
                  <TableCell className="granted-on-col">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>
                        {row.access_granted_on
                          ? moment(row.access_granted_on).format("MMMM Do YYYY")
                          : "—"}
                      </span>
                    </div>
                  </TableCell>

                  {/* Security Filter */}
                  <TableCell className="security-filter-col">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <span
                        className="aide-console-view-filters"
                        onClick={() => handleViewFilters(row)}
                      >
                        View Filters
                      </span>
                      {canEdit && (
                        <span
                          className="material-icons-outlined aide-console-delete-btn"
                          onClick={() => onDeleteUser(row)}
                        >
                          delete
                        </span>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        className="aide-console-pagination"
        rowsPerPageOptions={[10, 25, 100]}
        component="div"
        count={filteredList.length}
        labelRowsPerPage="Users per page:"
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {filteredList.length === 0 && (
        <div className="aide-console-no-users">No users found</div>
      )}

      {/* Security Filter Dialog */}
      {openFilter && selectedRow && (
        <AIDEConsoleSecurityFilter
          open={openFilter}
          handleClose={handleCloseFilter}
          row={selectedRow}
          countryBrandsData={countryBrandsData}
          accessList={accessList}
          onSaveFilters={handleSaveFilters}
          theme={theme}
        />
      )}
    </Paper>
  );
};

export default AIDEConsoleUserTable;
